import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spade, CalendarClock, BarChart3, BookOpen, ListChecks, Bot, ExternalLink, Users, Ticket, Trophy } from "lucide-react";
import { API, BALATRO_STORE_URL } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import ConversionCta from "@/components/ConversionCta";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CLUSTERS, launchInCluster, PLATFORM_META } from "@/seo/launchPages";

const TABS = [
  { id: "schedule", label: "Tournament Schedule", icon: CalendarClock },
  { id: "rankings", label: "Rankings", icon: BarChart3 },
  { id: "rules", label: "Rules", icon: BookOpen },
  { id: "results", label: "Results", icon: ListChecks },
  { id: "strategy", label: "Strategy", icon: Bot },
  { id: "get", label: "Get Balatro", icon: ExternalLink },
];

export default function BalatroHub() {
  const [tab, setTab] = useState("schedule");
  const [event, setEvent] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const navigate = useNavigate();
  usePageMeta(PLATFORM_META["/balatro"]);

  useEffect(() => {
    fetch(`${API}/next-event`).then((r) => r.json()).then(setEvent).catch(() => {});
    fetch(`${API}/leaderboard`).then((r) => r.json()).then((d) => setLeaders(d.leaderboard || [])).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-5 md:px-8 py-12 md:py-16">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-12 w-12 rounded-xl bg-gp-gold/15 text-gp-gold"><Spade size={24} /></span>
        <div>
          <span className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Community Hub</span>
          <h1 className="font-unbounded font-black tracking-tighter text-3xl sm:text-4xl uppercase leading-none">Balatro</h1>
        </div>
      </div>

      <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
        Thirty launch guides Yorick already has in the database — beginner academy, Jokers, decks, scoring questions, and how the tournament works. He posts and pastes the promos on a schedule. You do not log in to publish them.
      </p>
      <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="balatro-guide-clusters">
        {CLUSTERS.map((cluster) => {
          const pages = launchInCluster(cluster.id);
          if (pages.length === 0) return null;
          return (
            <div key={cluster.id} className="glass rounded-2xl p-5">
              <p className="text-xs uppercase tracking-widest text-gp-gold">{cluster.name}</p>
              <ul className="mt-3 space-y-2">
                {pages.map((page) => (
                  <li key={page.path}>
                    <Link to={page.path} className="text-sm text-white/80 hover:text-gp-blue">{page.h1}</Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto gp-scroll pb-1" data-testid="hub-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hub-tab-${t.id}`}
              className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${active ? "bg-gp-blue text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8" data-testid="hub-content">
        {tab === "schedule" && (
          <div className="glass rounded-2xl p-7">
            <span className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Upcoming Tournament</span>
            <h2 className="font-unbounded font-bold text-2xl mt-1">{event?.event?.name || "GamePlat Balatro Community Challenge"}</h2>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-white/75">
              <span className="flex items-center gap-1.5"><CalendarClock size={15} className="text-gp-gold" /> {event?.event?.date_label || "December 12, 2026"}</span>
              <span className="flex items-center gap-1.5"><Users size={15} className="text-gp-blue" /> Up to {event?.event?.players || 64} seats</span>
              <span className="flex items-center gap-1.5"><Ticket size={15} className="text-gp-green" /> {event?.event?.entry || "Free Entry"}</span>
              <span className="flex items-center gap-1.5"><Trophy size={15} className="text-gp-gold" /> {event?.event?.type || "Community Competition"}</span>
            </div>
            <p className="text-white/60 mt-4">{event?.event?.date_note}</p>
            <Button onClick={() => navigate("/tournaments")} className="mt-5 rounded-full bg-gp-blue font-bold px-6">Join tournament waitlist</Button>
          </div>
        )}
        {tab === "rankings" && (
          <div className="glass rounded-2xl p-2">
            {leaders.length === 0 ? <p className="text-white/45 text-sm p-6">No ranked players yet — win the first Community Challenge to top the board.</p> : (
              <div className="divide-y divide-white/10">
                {leaders.map((p, i) => (
                  <div key={p.user_id} className="flex items-center gap-3 px-5 py-3"><span className="w-6 text-center font-unbounded font-black text-gp-gold">{i + 1}</span><span className="flex-1 font-semibold">{p.name || "Player"}</span><span className="font-bold text-gp-blue">{p.points} pts</span></div>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === "rules" && (
          <div className="glass rounded-2xl p-7 space-y-3 text-white/75">
            <p><strong className="text-white">Format:</strong> Highest verified ante-8 score wins. Best legit run takes the crown.</p>
            <p><strong className="text-white">Proof:</strong> Submit a screenshot/clip of your final score. Keep your seed handy.</p>
            <p><strong className="text-white">Fair play:</strong> Standard decks only, no external mods or trainers. See the full <Link to="/rules" className="text-gp-blue underline">rules page</Link>.</p>
          </div>
        )}
        {tab === "results" && (
          <div className="glass rounded-2xl p-7 text-white/60">
            <p>Verified results from the Balatro Community Challenge will be posted here after the event. Registered players get notified when results go live.</p>
            <Button onClick={() => navigate("/leaderboard")} variant="outline" className="mt-4 rounded-full border-white/15 bg-white/5">View leaderboard</Button>
          </div>
        )}
        {tab === "strategy" && (
          <div className="glass rounded-2xl p-7">
            <p className="text-white/75">Launch guides are listed above. Yorick will also walk a combo live if you want a run-specific answer.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/chat")} className="rounded-full bg-gp-blue font-bold px-6"><Bot size={16} className="mr-2" /> Open Yorick chat</Button>
              <Button onClick={() => navigate("/builds/new")} variant="outline" className="rounded-full border-gp-gold/35 bg-gp-gold/10 text-gp-gold font-semibold" data-testid="share-build-link">Share your run build</Button>
            </div>
          </div>
        )}
        {tab === "get" && (
          <div className="glass rounded-2xl p-7">
            <h3 className="font-unbounded font-bold text-xl">Get Balatro</h3>
            <p className="text-white/65 mt-2">Balatro is made by LocalThunk and published by Playstack. Grab it from an official store — GamePlat is a community platform and does not distribute the game.</p>
            <a href={BALATRO_STORE_URL} target="_blank" rel="noreferrer" data-testid="get-balatro-link" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gp-gold hover:bg-gp-gold/90 text-gp-bg font-bold px-6 py-3 transition-colors">
              <ExternalLink size={17} /> Get Balatro on Steam
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
